import {
  APP_INITIALIZER,
  EnvironmentProviders,
  PLATFORM_ID,
  Provider,
  makeEnvironmentProviders,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '../../../environments/environment';

/**
 * Removes a service worker left behind on a development machine.
 *
 * Turning registration off stops NEW workers appearing, but does nothing about
 * one already installed: a service worker outlives the page, the tab and the
 * browser, and keeps controlling the origin until something unregisters it.
 *
 * That worker is fatal under `ng serve`. It validates every hashed asset
 * against the ngsw.json it cached, and the dev server rebuilds chunks with new
 * hashes constantly, so the first check fails:
 *
 *   SwCriticalError: Hash mismatch (cacheBustedFetchFromNetwork):
 *   /chunk-XXXX.js: expected <a>, got <b> (after cache busting)
 *
 * Because it survives the reload, reloading does not clear it - it re-checks,
 * mismatches, and takes the page down again. Worse, the failure is not obviously
 * a service-worker problem: it also swallows lazy-route chunks, so buttons that
 * navigate to lazily loaded pages simply stop working.
 *
 * Every developer who ran a production-config build against localhost has one of
 * these. Doing it in code is what makes that self-healing, rather than each of
 * them having to find Application > Service Workers > Unregister.
 *
 * Only the caches this worker owns are removed. Angular namespaces them under
 * `ngsw:`, so the app's own Cache Storage - and IndexedDB, which the offline
 * database uses - are left untouched.
 */
export function provideDevServiceWorkerCleanup(): EnvironmentProviders {
  const providers: Provider[] = [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [PLATFORM_ID],
      useFactory: (platformId: object) => () => {
        // Production is where the worker is meant to be running. Unregistering
        // there would disable offline support for real users.
        if (environment.production) return;
        if (!isPlatformBrowser(platformId)) return;
        if (!('serviceWorker' in navigator)) return;

        // Not awaited: bootstrap must not wait on this, and the page it is
        // rescuing has already loaded by the time it resolves.
        void cleanUpDevServiceWorkers();
      },
    },
  ];

  return makeEnvironmentProviders(providers);
}

async function cleanUpDevServiceWorkers(): Promise<void> {
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) return;

    await Promise.all(registrations.map((registration) => registration.unregister()));

    // The registration is gone but its caches are not, and a stale ngsw cache
    // can still be served to the page that is already open.
    if ('caches' in globalThis) {
      const names = await caches.keys();
      await Promise.all(
        names.filter((name) => name.startsWith('ngsw:')).map((name) => caches.delete(name)),
      );
    }

    console.warn(
      '[pwa] Removed %d service worker registration(s) left over from a production-config build. ' +
        'The page is still controlled by the old worker until you reload once more.',
      registrations.length,
    );
  } catch (error) {
    // Never break startup over cleanup. A browser that refuses this (private
    // mode, policy) is one where the worker could not have registered either.
    console.warn('[pwa] Could not clean up development service workers.', error);
  }
}
